/**
 * @fileoverview Componente móvil de aficionado para la dinámica Reacción Rápida Personal (Personal Quick Reaction - HU-04).
 * Experiencia táctil interactiva a 60 FPS con máquina de estados completa:
 * LOADING -> CLOSED / FORM_SCREEN -> COUNTDOWN -> IDLE (Espera con personaje de espaldas 1.png) ->
 * DRAW (¡FRENTE! con personaje 2.png y tono 1200 Hz) -> ANIMATING / RESULT_SCREEN.
 * Incluye Screen Wake Lock API, Web Audio API sintetizado, vibración háptica de 40ms,
 * estrategias anti-spam configurables, detección de inactividad, ranking en vivo y soporte de teléfono compartido.
 * 
 * @module components/Dashboard/Sections/PersonalQuickReactionFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SignalRService from '../../../utilities/WS/SignalRService';
import {
    GetPublicConfigBL,
    RegisterParticipantBL,
    SubmitPlayBL,
    createSynthesizedAudioEngine,
    requestScreenWakeLock,
    releaseScreenWakeLock,
    triggerHapticFeedback,
    preloadThemeAssets,
    getReactionBadge,
    trackDualTelemetry,
    getParticipantSession,
    saveParticipantSession,
    clearParticipantSession,
    validateRegistrationForm,
    sanitizeRoomId
} from '../../../utilities/BL/PersonalQuickReaction';
import PersonalQuickReactionRankingModal from './PersonalQuickReactionRankingModal';

/**
 * Normaliza y resuelve la URL completa de un asset multimedia del tema.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL absoluta normalizada.
 */
const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return '';
    const trimmed = mediaPath.trim();
    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('blob:') ||
        trimmed.startsWith('data:')
    ) {
        return trimmed;
    }

    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    return `${apiBaseUrl}${trimmed.startsWith('/') ? trimmed.substring(1) : trimmed}`;
};

/**
 * Componente principal móvil para Reacción Rápida Personal.
 * 
 * @function PersonalQuickReactionFan
 * @param {Object} [props] - Propiedades pasadas por router o contenedor SaaS.
 * @param {string} [props.roomId] - Identificador GUID de la sala.
 * @returns {React.ReactElement} Interfaz de usuario táctil móvil.
 */
export const PersonalQuickReactionFan = ({ roomId: propRoomId }) => {
    const location = useLocation();

    // =========================================================================
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA (ROOM ID) Y SESIÓN
    // =========================================================================
    const activeRoomId = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        const rawId = (
            propRoomId ||
            queryParams.get('roomId') ||
            (typeof localStorage !== 'undefined' ? localStorage.getItem('active_room_id') : '') ||
            ''
        );
        return sanitizeRoomId(rawId);
    }, [propRoomId, location.search]);

    // =========================================================================
    // 2. MÁQUINA DE ESTADOS Y CONTROL DE FLUJO
    // =========================================================================
    // Fases: 'LOADING' | 'CLOSED' | 'FORM_SCREEN' | 'ALREADY_PARTICIPATED' | 'COUNTDOWN' | 'IDLE' | 'DRAW' | 'ANIMATING' | 'RESULT_SCREEN'
    const [gameState, setGameState] = useState('LOADING');

    const [configData, setConfigData] = useState(null);
    const [themeData, setThemeData] = useState(null);

    // Formulario de registro
    const [formData, setFormData] = useState({
        participantName: '',
        email: '',
        phone: '',
        acceptedTerms: false,
        acceptedPrivacy: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);

    // Datos del participante autenticado con OTT
    const [participantSession, setParticipantSession] = useState(null);

    // Estados de juego en tiempo real
    const [countdownValue, setCountdownValue] = useState(3);
    const [penaltyTimeMs, setPenaltyTimeMs] = useState(0);
    const [earlyAttemptsCount, setEarlyAttemptsCount] = useState(0);
    const [spamWarningMessage, setSpamWarningMessage] = useState('');
    const [isSpamShaking, setIsSpamShaking] = useState(false);

    // Resultado final de la jugada
    const [playResult, setPlayResult] = useState(null);

    // Modales secundarios
    const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

    // Control de audio
    const audioEngineRef = useRef(null);
    const [isMuted, setIsMuted] = useState(false);

    // Referencias mutables de alta precisión (60 FPS / performance.now)
    const triggerStartPerfMsRef = useRef(0);
    const inactivityTimeoutTimerRef = useRef(null);
    const randomWaitTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const sessionIdRef = useRef('pqr_session_' + Math.random().toString(36).substring(2, 10));
    const lastTapTimestampRef = useRef(0);

    // Inicialización del motor de audio sintético
    useEffect(() => {
        const engine = createSynthesizedAudioEngine();
        audioEngineRef.current = engine;
        setIsMuted(engine.isMuted());

        return () => {
            engine.dispose();
            releaseScreenWakeLock();
            if (inactivityTimeoutTimerRef.current) clearTimeout(inactivityTimeoutTimerRef.current);
            if (randomWaitTimerRef.current) clearTimeout(randomWaitTimerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, []);

    // Conexión y sincronización SignalR para la sala
    useEffect(() => {
        if (!activeRoomId) return;

        SignalRService.connect({ roomId: activeRoomId, tipo: 'fan' }).catch(() => {});

        const handleConfigUpdated = (data) => {
            if (data) {
                setConfigData(data);
                if (data.isActive === false) {
                    setGameState('CLOSED');
                } else if (data.isActive === true) {
                    setGameState((prev) => (prev === 'CLOSED' ? 'FORM_SCREEN' : prev));
                }
                if (data.activeTheme) {
                    setThemeData(data.activeTheme);
                    preloadThemeAssets(data.activeTheme);
                }
            }
        };

        const handleResetEvent = (data) => {
            if (data?.resetType === 'FULL') {
                clearParticipantSession(activeRoomId);
                setParticipantSession(null);
                setPlayResult(null);
                setGameState('FORM_SCREEN');
            }
        };

        SignalRService.on('personalQuickReaction:configUpdated', handleConfigUpdated);
        SignalRService.on('personalQuickReaction:reset', handleResetEvent);

        return () => {
            SignalRService.off('personalQuickReaction:configUpdated', handleConfigUpdated);
            SignalRService.off('personalQuickReaction:reset', handleResetEvent);
        };
    }, [activeRoomId]);

    // =========================================================================
    // 3. CARGA INICIAL DE CONFIGURACIÓN Y VERIFICACIÓN DE SESIÓN LOCAL
    // =========================================================================
    const initializeGame = useCallback(async () => {
        if (!activeRoomId) {
            setGameState('FORM_SCREEN');
            return;
        }

        try {
            const configResponse = await GetPublicConfigBL(activeRoomId);
            if (configResponse.success && configResponse.data) {
                const cfg = configResponse.data;
                setConfigData(cfg);

                if (!cfg.isActive) {
                    setGameState('CLOSED');
                    return;
                }

                if (cfg.activeTheme) {
                    setThemeData(cfg.activeTheme);
                    preloadThemeAssets(cfg.activeTheme);
                }
            }

            // Verificar si el participante ya tiene una sesión previa guardada
            const savedSession = getParticipantSession(activeRoomId);
            if (savedSession && savedSession.participantId) {
                setParticipantSession(savedSession);
                if (savedSession.isPlayed && savedSession.playResult) {
                    setPlayResult(savedSession.playResult);
                    setGameState('ALREADY_PARTICIPATED');
                    return;
                }
            }

            setGameState('FORM_SCREEN');
            trackDualTelemetry({
                roomId: activeRoomId,
                sessionId: sessionIdRef.current,
                stepName: 'FORM_VIEW'
            });
        } catch (err) {
            setGameState('FORM_SCREEN');
        }
    }, [activeRoomId]);

    useEffect(() => {
        initializeGame();
    }, [initializeGame]);

    // =========================================================================
    // 4. MANEJO DE FORMULARIO DE REGISTRO
    // =========================================================================
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));

        if (formErrors[field]) {
            setFormErrors((prev) => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    const handleFormSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Desbloquear AudioContext en la primera interacción táctil
        if (audioEngineRef.current) {
            audioEngineRef.current.unlock();
        }

        const validation = validateRegistrationForm(formData);
        if (!validation.isValid) {
            setFormErrors(validation.errors);
            triggerHapticFeedback('fault');
            return;
        }

        setIsSubmittingForm(true);

        try {
            trackDualTelemetry({
                roomId: activeRoomId,
                sessionId: sessionIdRef.current,
                stepName: 'FORM_SUBMIT',
                additionalData: { email: formData.email }
            });

            const registerPayload = {
                roomId: activeRoomId,
                participantName: formData.participantName.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim(),
                acceptedTerms: formData.acceptedTerms,
                acceptedPrivacy: formData.acceptedPrivacy
            };

            const response = await RegisterParticipantBL(registerPayload);

            if (!response.success && response.data?.status === 'INACTIVE') {
                setGameState('CLOSED');
                return;
            }

            if (response.success && response.data) {
                const responseData = response.data;

                // Caso: El aficionado ya completó su jugada previamente
                if (responseData.status === 'ALREADY_PLAYED' && responseData.previousPlayResult) {
                    const sessionToSave = {
                        participantId: responseData.participantId,
                        participantName: responseData.participantName,
                        roomId: activeRoomId,
                        isPlayed: true,
                        playResult: responseData.previousPlayResult
                    };
                    saveParticipantSession(sessionToSave);
                    setParticipantSession(sessionToSave);
                    setPlayResult(responseData.previousPlayResult);
                    setGameState('ALREADY_PARTICIPATED');
                    return;
                }

                // Caso: Registro exitoso con emisión de OTT de 5 minutos
                const sessionToSave = {
                    participantId: responseData.participantId,
                    participantName: responseData.participantName,
                    oneTimeToken: responseData.oneTimeToken,
                    roomId: activeRoomId,
                    isPlayed: false
                };

                saveParticipantSession(sessionToSave);
                setParticipantSession(sessionToSave);

                // Iniciar la secuencia de conteo del juego
                startCountdownSequence();
            } else {
                setFormErrors({
                    general: response.message || 'No se pudo completar el registro. Intenta nuevamente.'
                });
                triggerHapticFeedback('fault');
            }
        } catch (submitError) {
            setFormErrors({
                general: 'Error de red o conexión al registrar datos.'
            });
        } finally {
            setIsSubmittingForm(false);
        }
    };

    // =========================================================================
    // 5. SECUENCIA DE JUEGO: CONTEO (3, 2, 1) -> ESPERA -> DISPARO ("¡FRENTE!")
    // =========================================================================

    /**
     * Inicia el conteo 3, 2, 1 y solicita Screen Wake Lock para mantener encendida la pantalla.
     */
    const startCountdownSequence = useCallback(() => {
        setGameState('COUNTDOWN');
        setCountdownValue(3);
        setPenaltyTimeMs(0);
        setEarlyAttemptsCount(0);
        setSpamWarningMessage('');

        requestScreenWakeLock();

        trackDualTelemetry({
            roomId: activeRoomId,
            sessionId: sessionIdRef.current,
            stepName: 'GAME_START'
        });

        if (audioEngineRef.current) {
            audioEngineRef.current.playCountBeep(3);
        }
        triggerHapticFeedback('tap');

        let currentCount = 3;
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        countdownIntervalRef.current = setInterval(() => {
            currentCount -= 1;
            if (currentCount > 0) {
                setCountdownValue(currentCount);
                if (audioEngineRef.current) {
                    audioEngineRef.current.playCountBeep(currentCount);
                }
                triggerHapticFeedback('tap');
            } else {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
                startRandomWaitPhase();
            }
        }, 1000);
    }, [activeRoomId]);

    /**
     * Inicia la fase de espera aleatoria con el personaje de espaldas (1.png) y sonido de tensión.
     */
    const startRandomWaitPhase = useCallback(() => {
        setGameState('IDLE');

        if (audioEngineRef.current) {
            audioEngineRef.current.playTension();
        }

        const minDelay = configData?.minWaitDelayMs || 3000;
        const maxDelay = configData?.maxWaitDelayMs || 6000;
        const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;

        if (randomWaitTimerRef.current) clearTimeout(randomWaitTimerRef.current);

        randomWaitTimerRef.current = setTimeout(() => {
            triggerDrawSignal();
        }, randomDelay);
    }, [configData]);

    /**
     * Emite la señal de disparo/trigger ("¡FRENTE!"), destello visual y personaje de frente (2.png).
     */
    const triggerDrawSignal = useCallback(() => {
        setGameState('DRAW');
        triggerStartPerfMsRef.current = performance.now();

        if (audioEngineRef.current) {
            audioEngineRef.current.playTrigger();
        }

        // Vibración háptica penetrante de 40ms según especificación
        triggerHapticFeedback('trigger');

        // Configurar temporizador de inactividad (5 a 10s según config, defecto 7s)
        const timeoutSeconds = configData?.inactivityTimeoutSeconds || 7;
        if (inactivityTimeoutTimerRef.current) clearTimeout(inactivityTimeoutTimerRef.current);

        inactivityTimeoutTimerRef.current = setTimeout(() => {
            handleInactivityTimeout();
        }, timeoutSeconds * 1000);
    }, [configData]);

    // =========================================================================
    // 6. GESTIÓN DE PULSACIONES TÁCTILES Y ESTRATEGIAS ANTI-SPAM
    // =========================================================================

    /**
     * Maneja las pulsaciones tempranas (antes del trigger) aplicando la estrategia anti-spam configurada.
     */
    const handleEarlyTap = useCallback(() => {
        const now = Date.now();
        if (now - lastTapTimestampRef.current < 250) {
            return; // Filtro de rebote mecánico de pantalla
        }
        lastTapTimestampRef.current = now;

        const strategy = configData?.earlyFaultStrategy || 'NONE';
        const newEarlyCount = earlyAttemptsCount + 1;
        setEarlyAttemptsCount(newEarlyCount);

        setIsSpamShaking(true);
        setTimeout(() => setIsSpamShaking(false), 400);

        if (audioEngineRef.current) {
            audioEngineRef.current.playFalseStart();
        }
        triggerHapticFeedback('fault');

        switch (strategy) {
            case 'STRICT': {
                // Descalificación inmediata por salida en falso
                if (randomWaitTimerRef.current) clearTimeout(randomWaitTimerRef.current);
                submitFinalPlayResult(0, 0, newEarlyCount, true, false);
                break;
            }

            case 'TIME_PENALTY': {
                // Añade penalización de +250ms al tiempo final
                const addedPenalty = 250;
                setPenaltyTimeMs((prev) => prev + addedPenalty);
                setSpamWarningMessage(`⚠️ ¡Demasiado pronto! (+${addedPenalty}ms de penalización)`);
                setTimeout(() => setSpamWarningMessage(''), 1800);
                break;
            }

            case 'RESET_DELAY': {
                // Reinicia el temporizador de espera aleatoria desde cero
                setSpamWarningMessage('⚠️ ¡Salida en falso! El tiempo de espera se ha reiniciado.');
                setTimeout(() => setSpamWarningMessage(''), 1800);
                if (randomWaitTimerRef.current) clearTimeout(randomWaitTimerRef.current);
                startRandomWaitPhase();
                break;
            }

            case 'THROTTLE': {
                setSpamWarningMessage('⚠️ ¡Espera la señal para desenfundar!');
                setTimeout(() => setSpamWarningMessage(''), 1500);
                break;
            }

            case 'NONE':
            default: {
                // No hace nada, ignora la pulsación antes del disparo
                break;
            }
        }
    }, [configData, earlyAttemptsCount, startRandomWaitPhase]);

    /**
     * Maneja el toque reactivo sobre la superficie de juego durante el trigger activo ("¡FRENTE!").
     */
    const handleReactionTap = useCallback(() => {
        if (gameState !== 'DRAW') {
            if (gameState === 'IDLE') {
                handleEarlyTap();
            }
            return;
        }

        // Medición milimétrica de tiempo con performance.now()
        const tapPerfMs = performance.now();
        const reactionMs = Math.round(tapPerfMs - triggerStartPerfMsRef.current);

        // Cancelar temporizador de inactividad y liberar Screen Wake Lock
        if (inactivityTimeoutTimerRef.current) {
            clearTimeout(inactivityTimeoutTimerRef.current);
            inactivityTimeoutTimerRef.current = null;
        }
        releaseScreenWakeLock();

        setGameState('ANIMATING');
        triggerHapticFeedback('tap');

        // Umbral fisiológico humano (mínimo 120 ms)
        const minThreshold = configData?.minHumanReactionThresholdMs || 120;
        const isBelowThreshold = reactionMs < minThreshold;

        submitFinalPlayResult(reactionMs, penaltyTimeMs, earlyAttemptsCount, isBelowThreshold, false);
    }, [gameState, configData, penaltyTimeMs, earlyAttemptsCount, handleEarlyTap]);

    /**
     * Maneja el vencimiento del temporizador de inactividad (5-10s).
     */
    const handleInactivityTimeout = useCallback(() => {
        releaseScreenWakeLock();
        if (audioEngineRef.current) {
            audioEngineRef.current.stopTension();
        }

        const timeoutLimitMs = (configData?.inactivityTimeoutSeconds || 7) * 1000;
        submitFinalPlayResult(timeoutLimitMs, penaltyTimeMs, earlyAttemptsCount, false, true);
    }, [configData, penaltyTimeMs, earlyAttemptsCount]);

    /**
     * Envía la jugada al backend con el One-Time Token (OTT) y actualiza el estado de resultados.
     */
    const submitFinalPlayResult = async (reactionMs, penaltyMs, earlyCount, isEarlyFault, isTimeout) => {
        const participantId = participantSession?.participantId;
        const oneTimeToken = participantSession?.oneTimeToken;

        if (!participantId || !oneTimeToken) {
            // Fallback de resultado local si no hubiera token disponible
            const fallbackBadge = getReactionBadge(reactionMs, isEarlyFault, isEarlyFault, isTimeout);
            const localResult = {
                participantName: participantSession?.participantName || 'Aficionado',
                reactionTimeMs: reactionMs,
                penaltyTimeMs: penaltyMs,
                totalTimeMs: reactionMs + penaltyMs,
                badgeName: fallbackBadge.name,
                isEarlyFault: isEarlyFault,
                isWinner: false,
                rankingPosition: 0,
                totalParticipants: 1
            };
            setPlayResult(localResult);
            setGameState('RESULT_SCREEN');
            return;
        }

        try {
            const submitPayload = {
                participantId: participantId,
                oneTimeToken: oneTimeToken,
                reactionTimeMs: reactionMs,
                penaltyTimeMs: penaltyMs,
                earlyAttemptsCount: earlyCount,
                isEarlyFault: isEarlyFault,
                clientTimestampUtc: new Date().toISOString()
            };

            const response = await SubmitPlayBL(submitPayload);

            if (response.success && response.data) {
                const resultData = response.data;
                setPlayResult(resultData);

                // Guardar resultado en sesión local para evitar re-jugadas con el mismo token
                const updatedSession = {
                    ...participantSession,
                    isPlayed: true,
                    playResult: resultData
                };
                saveParticipantSession(updatedSession);
                setParticipantSession(updatedSession);

                if (resultData.isEarlyFault) {
                    if (audioEngineRef.current) audioEngineRef.current.playFalseStart();
                    triggerHapticFeedback('fault');
                } else if (!isTimeout) {
                    if (audioEngineRef.current) audioEngineRef.current.playVictory();
                    triggerHapticFeedback('victory');
                }

                trackDualTelemetry({
                    roomId: activeRoomId,
                    sessionId: sessionIdRef.current,
                    stepName: 'RESULT_VIEW',
                    additionalData: {
                        reactionTimeMs: resultData.reactionTimeMs,
                        totalTimeMs: resultData.totalTimeMs,
                        badgeName: resultData.badgeName
                    }
                });
            } else {
                // Fallback en caso de error de red
                const fallbackBadge = getReactionBadge(reactionMs, isEarlyFault, isEarlyFault, isTimeout);
                const localResult = {
                    participantName: participantSession?.participantName || 'Aficionado',
                    reactionTimeMs: reactionMs,
                    penaltyTimeMs: penaltyMs,
                    totalTimeMs: reactionMs + penaltyMs,
                    badgeName: fallbackBadge.name,
                    isEarlyFault: isEarlyFault,
                    isWinner: false,
                    rankingPosition: 0,
                    totalParticipants: 1
                };
                setPlayResult(localResult);
            }
        } catch {
            const fallbackBadge = getReactionBadge(reactionMs, isEarlyFault, isEarlyFault, isTimeout);
            setPlayResult({
                participantName: participantSession?.participantName || 'Aficionado',
                reactionTimeMs: reactionMs,
                penaltyTimeMs: penaltyMs,
                totalTimeMs: reactionMs + penaltyMs,
                badgeName: fallbackBadge.name,
                isEarlyFault: isEarlyFault,
                isWinner: false,
                rankingPosition: 0,
                totalParticipants: 1
            });
        } finally {
            setGameState('RESULT_SCREEN');
        }
    };

    // =========================================================================
    // 7. ACCIONES DE DISPOSITIVO COMPARTIDO (SHARED DEVICE) Y SILENCIO
    // =========================================================================

    /**
     * Permite que otro participante juegue en el mismo dispositivo móvil (Shared Device).
     */
    const handleResetForSharedDevice = () => {
        clearParticipantSession(activeRoomId);
        setParticipantSession(null);
        setPlayResult(null);
        setFormData({
            participantName: '',
            email: '',
            phone: '',
            acceptedTerms: false,
            acceptedPrivacy: false
        });
        setFormErrors({});
        setPenaltyTimeMs(0);
        setEarlyAttemptsCount(0);
        setGameState('FORM_SCREEN');
    };

    /**
     * Alterna el estado de silencio (Mute / Unmute).
     */
    const handleToggleMute = () => {
        if (audioEngineRef.current) {
            const nextMuted = !audioEngineRef.current.isMuted();
            audioEngineRef.current.setMuted(nextMuted);
            setIsMuted(nextMuted);
        }
    };

    // URLs de sprites y recursos del tema activo
    const backgroundUrl = themeData?.backgroundImageUrl ? getFullAssetUrl(themeData.backgroundImageUrl) : '';
    const logoUrl = themeData?.logoImageUrl ? getFullAssetUrl(themeData.logoImageUrl) : '';
    const spriteBackUrl = themeData?.spriteBackUrl
        ? getFullAssetUrl(themeData.spriteBackUrl)
        : '/assets/quick-reaction/cowboy-back.png';
    const spriteFrontUrl = themeData?.spriteFrontUrl
        ? getFullAssetUrl(themeData.spriteFrontUrl)
        : '/assets/quick-reaction/cowboy-front.png';

    // =========================================================================
    // 8. RENDERIZADO PRINCIPAL DE PANTALLAS SEGÚN MÁQUINA DE ESTADOS
    // =========================================================================

    return (
        <div
            className="pqr-fan-wrapper"
            style={{
                backgroundImage: backgroundUrl ? `url("${backgroundUrl}")` : undefined
            }}
        >
            {/* CABECERA SUPERIOR FLOTANTE */}
            <header className="pqr-header">
                <div className="pqr-header__brand">
                    {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="pqr-header__logo" />
                    ) : (
                        <span className="pqr-header__title-text">⚡ Reacción Rápida</span>
                    )}
                </div>

                <div className="pqr-header__actions">
                    <button
                        type="button"
                        className={`pqr-header__mute-btn ${isMuted ? 'is-muted' : ''}`}
                        onClick={handleToggleMute}
                        title={isMuted ? 'Activar sonido' : 'Silenciar'}
                        aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
                    >
                        <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`} />
                    </button>
                </div>
            </header>

            {/* AVISO DE SPAM / FALTA TEMPRANA */}
            {spamWarningMessage && (
                <div className="pqr-toast-warning">
                    {spamWarningMessage}
                </div>
            )}

            {/* ESTADO 1: CARGANDO */}
            {gameState === 'LOADING' && (
                <main className="pqr-screen pqr-screen--loading">
                    <div className="pqr-spinner-lg" />
                    <p className="pqr-screen__subtitle">Cargando experiencia de juego...</p>
                </main>
            )}

            {/* ESTADO 2: DINÁMICA CERRADA / PAUSADA */}
            {gameState === 'CLOSED' && (
                <main className="pqr-screen pqr-screen--closed">
                    <div className="pqr-card-glass">
                        <div className="pqr-icon-circle pqr-icon-circle--paused">
                            <i className="bi bi-pause-fill" />
                        </div>
                        <h2 className="pqr-card__title">Dinámica en Pausa</h2>
                        <p className="pqr-card__text">
                            La dinámica de Reacción Rápida se encuentra pausada temporalmente en esta sala.
                            ¡Mantén esta ventana abierta o regresa en unos momentos!
                        </p>
                    </div>
                </main>
            )}

            {/* ESTADO 3: FORMULARIO DE REGISTRO */}
            {gameState === 'FORM_SCREEN' && (
                <main className="pqr-screen pqr-screen--form">
                    <div className="pqr-card-glass pqr-card-glass--form">
                        <div className="pqr-card__header">
                            <span className="pqr-card__badge">Desafío Individual</span>
                            <h2 className="pqr-card__title">¿Qué tan rápido eres?</h2>
                            <p className="pqr-card__subtitle">
                                Registra tus datos para participar y medir tus reflejos en milisegundos.
                            </p>
                        </div>

                        <form className="pqr-form" onSubmit={handleFormSubmit} noValidate>
                            {formErrors.general && (
                                <div className="pqr-form__error-alert">
                                    <i className="bi bi-exclamation-triangle-fill" /> {formErrors.general}
                                </div>
                            )}

                            <div className="pqr-form__group">
                                <label className="pqr-form__label" htmlFor="pqr-name">
                                    Nombre Completo:
                                </label>
                                <input
                                    id="pqr-name"
                                    type="text"
                                    className={`pqr-form__input ${formErrors.participantName ? 'is-invalid' : ''}`}
                                    placeholder="Ej. Carlos Martínez"
                                    value={formData.participantName}
                                    onChange={(e) => handleInputChange('participantName', e.target.value)}
                                    maxLength={200}
                                    autoComplete="name"
                                    disabled={isSubmittingForm}
                                />
                                {formErrors.participantName && (
                                    <span className="pqr-form__error-msg">{formErrors.participantName}</span>
                                )}
                            </div>

                            <div className="pqr-form__group">
                                <label className="pqr-form__label" htmlFor="pqr-email">
                                    Correo Electrónico:
                                </label>
                                <input
                                    id="pqr-email"
                                    type="email"
                                    className={`pqr-form__input ${formErrors.email ? 'is-invalid' : ''}`}
                                    placeholder="ejemplo@correo.com"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    maxLength={200}
                                    autoComplete="email"
                                    disabled={isSubmittingForm}
                                />
                                {formErrors.email && (
                                    <span className="pqr-form__error-msg">{formErrors.email}</span>
                                )}
                            </div>

                            <div className="pqr-form__group">
                                <label className="pqr-form__label" htmlFor="pqr-phone">
                                    Teléfono Celular / WhatsApp:
                                </label>
                                <input
                                    id="pqr-phone"
                                    type="tel"
                                    className={`pqr-form__input ${formErrors.phone ? 'is-invalid' : ''}`}
                                    placeholder="10 dígitos (ej. 6641234567)"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    maxLength={20}
                                    autoComplete="tel"
                                    disabled={isSubmittingForm}
                                />
                                {formErrors.phone && (
                                    <span className="pqr-form__error-msg">{formErrors.phone}</span>
                                )}
                            </div>

                            <div className="pqr-form__checkboxes">
                                <label className="pqr-form__checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="pqr-form__checkbox"
                                        checked={formData.acceptedTerms}
                                        onChange={(e) => handleInputChange('acceptedTerms', e.target.checked)}
                                        disabled={isSubmittingForm}
                                    />
                                    <span>
                                        Acepto los{' '}
                                        <button
                                            type="button"
                                            className="pqr-form__link-btn"
                                            onClick={() => setIsTermsModalOpen(true)}
                                        >
                                            Términos y Condiciones
                                        </button>
                                    </span>
                                </label>
                                {formErrors.acceptedTerms && (
                                    <span className="pqr-form__error-msg">{formErrors.acceptedTerms}</span>
                                )}

                                <label className="pqr-form__checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="pqr-form__checkbox"
                                        checked={formData.acceptedPrivacy}
                                        onChange={(e) => handleInputChange('acceptedPrivacy', e.target.checked)}
                                        disabled={isSubmittingForm}
                                    />
                                    <span>
                                        Acepto el{' '}
                                        <button
                                            type="button"
                                            className="pqr-form__link-btn"
                                            onClick={() => setIsPrivacyModalOpen(true)}
                                        >
                                            Aviso de Privacidad
                                        </button>
                                    </span>
                                </label>
                                {formErrors.acceptedPrivacy && (
                                    <span className="pqr-form__error-msg">{formErrors.acceptedPrivacy}</span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="pqr-btn-primary"
                                disabled={isSubmittingForm}
                            >
                                {isSubmittingForm ? (
                                    <>
                                        <span className="pqr-spinner-sm" /> Validando pase...
                                    </>
                                ) : (
                                    '¡Entrar al Desafío!'
                                )}
                            </button>
                        </form>
                    </div>
                </main>
            )}

            {/* ESTADO 4: YA PARTICIPÓ ANTERIORMENTE */}
            {gameState === 'ALREADY_PARTICIPATED' && (
                <main className="pqr-screen pqr-screen--already">
                    <div className="pqr-card-glass">
                        <div className="pqr-icon-circle pqr-icon-circle--checked">
                            <i className="bi bi-check-lg" />
                        </div>
                        <span className="pqr-card__badge">Pase Registrado</span>
                        <h2 className="pqr-card__title">¡Ya Has Participado!</h2>
                        <p className="pqr-card__subtitle">
                            Hola <strong>{participantSession?.participantName || 'Aficionado'}</strong>, ya registraste tu intento para esta jornada.
                        </p>

                        {playResult && (
                            <div className="pqr-result-box">
                                <div className="pqr-result-box__row">
                                    <span className="pqr-result-box__label">Tiempo Registrado:</span>
                                    <span className="pqr-result-box__value">
                                        {playResult.totalTimeMs || playResult.reactionTimeMs} ms
                                    </span>
                                </div>
                                <div className="pqr-result-box__row">
                                    <span className="pqr-result-box__label">Insignia:</span>
                                    <span className="pqr-result-box__value pqr-result-box__badge">
                                        {playResult.badgeName}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="pqr-card__actions">
                            <button
                                type="button"
                                className="pqr-btn-primary"
                                onClick={() => setIsRankingModalOpen(true)}
                            >
                                🏆 Ver Top 20 en Vivo
                            </button>

                            {(configData?.allowSharedDevice !== false) && (
                                <button
                                    type="button"
                                    className="pqr-btn-outline"
                                    onClick={handleResetForSharedDevice}
                                >
                                    ¿Alguien más quiere jugar en este teléfono?
                                </button>
                            )}
                        </div>
                    </div>
                </main>
            )}

            {/* ESTADO 5: CONTEO REGRESIVO (3, 2, 1) */}
            {gameState === 'COUNTDOWN' && (
                <main className="pqr-screen pqr-screen--countdown">
                    <div className="pqr-countdown-container">
                        <h3 className="pqr-countdown__hint">¡PREPÁRATE!</h3>
                        <div key={countdownValue} className="pqr-countdown__number">
                            {countdownValue}
                        </div>
                        <p className="pqr-countdown__instructions">
                            Mantén el dedo listo sobre la pantalla y presiona en cuanto veas <strong>¡FRENTE!</strong>
                        </p>
                    </div>
                </main>
            )}

            {/* ESTADOS 6 Y 7: ESPERA ALEATORIA (IDLE) O DISPARO ("¡FRENTE!" / DRAW) */}
            {(gameState === 'IDLE' || gameState === 'DRAW' || gameState === 'ANIMATING') && (
                <main
                    className={`pqr-screen pqr-screen--gameplay ${gameState === 'DRAW' ? 'is-draw-active' : ''} ${isSpamShaking ? 'is-shaking' : ''}`}
                    onPointerDown={handleReactionTap}
                >
                    {/* SUPERFICIE TÁCTIL A PANTALLA COMPLETA */}
                    <div className="pqr-gameplay-surface">
                        <div className="pqr-gameplay-status">
                            {gameState === 'IDLE' ? (
                                <div className="pqr-gameplay-badge pqr-gameplay-badge--wait">
                                    <i className="bi bi-clock-history" /> ESPERA LA SEÑAL...
                                </div>
                            ) : (
                                <div className="pqr-gameplay-badge pqr-gameplay-badge--shoot">
                                    ¡¡ DESENFUNDA AHORA !!
                                </div>
                            )}
                        </div>

                        {/* CONTENEDOR DE PERSONAJE CON SPRITE 1 (ESPALDAS) O 2 (FRENTE) */}
                        <div className="pqr-character-box">
                            {gameState === 'IDLE' ? (
                                <img
                                    src={spriteBackUrl}
                                    alt="Personaje de espaldas"
                                    className="pqr-character-sprite pqr-character-sprite--back"
                                    draggable="false"
                                />
                            ) : (
                                <img
                                    src={spriteFrontUrl}
                                    alt="Personaje de frente"
                                    className="pqr-character-sprite pqr-character-sprite--front"
                                    draggable="false"
                                />
                            )}
                        </div>

                        <div className="pqr-tap-cue">
                            {gameState === 'IDLE' ? (
                                <p className="pqr-tap-cue__text">No toques la pantalla antes de tiempo...</p>
                            ) : (
                                <div className="pqr-trigger-label">
                                    ¡¡ FRENTE !!
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            )}

            {/* ESTADO 8: PANTALLA DE RESULTADOS */}
            {gameState === 'RESULT_SCREEN' && playResult && (
                <main className="pqr-screen pqr-screen--result">
                    <div className="pqr-card-glass pqr-card-glass--result">
                        {(() => {
                            const badge = getReactionBadge(
                                playResult.reactionTimeMs,
                                playResult.isEarlyFault,
                                playResult.reactionTimeMs < (configData?.minHumanReactionThresholdMs || 120),
                                playResult.outcomeStatus === 'TIMEOUT'
                            );

                            return (
                                <>
                                    <div
                                        className="pqr-badge-circle"
                                        style={{ borderColor: badge.color, boxShadow: `0 0 35px ${badge.color}66` }}
                                    >
                                        <i className={`bi ${badge.icon}`} style={{ color: badge.color }} />
                                    </div>

                                    <h2 className="pqr-result-title">
                                        {playResult.isEarlyFault ? '¡Salida en Falso!' : '¡Tiempo de Reacción!'}
                                    </h2>

                                    <div className="pqr-result-time-display">
                                        <span className="pqr-result-time-value" style={{ color: badge.color }}>
                                            {playResult.totalTimeMs || playResult.reactionTimeMs}
                                        </span>
                                        <span className="pqr-result-time-unit">ms</span>
                                    </div>

                                    {playResult.penaltyTimeMs > 0 && (
                                        <div className="pqr-penalty-indicator">
                                            Tiempo base: {playResult.reactionTimeMs}ms + {playResult.penaltyTimeMs}ms penaliz.
                                        </div>
                                    )}

                                    <div className="pqr-badge-banner" style={{ borderColor: badge.color }}>
                                        <span className="pqr-badge-name" style={{ color: badge.color }}>
                                            {badge.name}
                                        </span>
                                        <p className="pqr-badge-desc">{badge.description}</p>
                                    </div>

                                    {playResult.rankingPosition > 0 && (
                                        <div className="pqr-ranking-position-pill">
                                            <i className="bi bi-trophy-fill" /> Posición #{playResult.rankingPosition} de {playResult.totalParticipants}
                                        </div>
                                    )}

                                    <div className="pqr-card__actions">
                                        <button
                                            type="button"
                                            className="pqr-btn-primary"
                                            onClick={() => setIsRankingModalOpen(true)}
                                        >
                                            🏆 Ver Top 20 en Vivo
                                        </button>

                                        {(configData?.allowSharedDevice !== false) && (
                                            <button
                                                type="button"
                                                className="pqr-btn-outline"
                                                onClick={handleResetForSharedDevice}
                                            >
                                                ¿Alguien más quiere jugar en este teléfono?
                                            </button>
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </main>
            )}

            {/* MODAL DEL TOP 20 EN VIVO */}
            <PersonalQuickReactionRankingModal
                isOpen={isRankingModalOpen}
                onClose={() => setIsRankingModalOpen(false)}
                roomId={activeRoomId}
                currentParticipantId={participantSession?.participantId}
            />

            {/* MODAL DE TÉRMINOS Y CONDICIONES */}
            {isTermsModalOpen && (
                <div className="pqr-modal-overlay" onClick={() => setIsTermsModalOpen(false)}>
                    <div className="pqr-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="pqr-modal-card__header">
                            <h3 className="pqr-modal-card__title">Términos y Condiciones Oficiales</h3>
                            <button
                                type="button"
                                className="pqr-modal-card__close-btn"
                                onClick={() => setIsTermsModalOpen(false)}
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>
                        <div className="pqr-modal-card__body pqr-modal-card__body--text">
                            <p><strong>1. PARTICIPACIÓN:</strong> Dinámica individual para aficionados. Límite de 1 registro por aficionado por jornada.</p>
                            <p><strong>2. MECÁNICA:</strong> Tras el conteo regresivo (3, 2, 1), el aficionado debe esperar la señal visual y auditiva ("¡FRENTE!") para tocar la pantalla.</p>
                            <p><strong>3. VALIDEZ:</strong> Pulsaciones por debajo de 120 ms se consideran salidas anticipadas fisiológicamente inviables y quedan descalificadas del ranking oficial.</p>
                            <p><strong>4. PREMIACIÓN:</strong> Los resultados oficiales serán validados por el comité de la sala en tiempo real.</p>
                        </div>
                        <div className="pqr-modal-card__footer">
                            <button
                                type="button"
                                className="pqr-modal-card__btn-close"
                                onClick={() => setIsTermsModalOpen(false)}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE AVISO DE PRIVACIDAD */}
            {isPrivacyModalOpen && (
                <div className="pqr-modal-overlay" onClick={() => setIsPrivacyModalOpen(false)}>
                    <div className="pqr-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="pqr-modal-card__header">
                            <h3 className="pqr-modal-card__title">Aviso de Privacidad</h3>
                            <button
                                type="button"
                                className="pqr-modal-card__close-btn"
                                onClick={() => setIsPrivacyModalOpen(false)}
                            >
                                <i className="bi bi-x-lg" />
                            </button>
                        </div>
                        <div className="pqr-modal-card__body pqr-modal-card__body--text">
                            <p>Los datos recabados (nombre, correo, teléfono) son tratados exclusivamente para registrar tu participación, validar tu identidad en caso de resultar ganador y generar estadísticas agregadas de la dinámica.</p>
                            <p>No comercializamos tu información con terceros no autorizados. Puedes solicitar la eliminación de tus datos en cualquier momento.</p>
                        </div>
                        <div className="pqr-modal-card__footer">
                            <button
                                type="button"
                                className="pqr-modal-card__btn-close"
                                onClick={() => setIsPrivacyModalOpen(false)}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalQuickReactionFan;
